/**
 * Every figure the help panel and the walkthroughs can draw, by name, with the sentence that stands in
 * for it.
 *
 * A union and a `Record` for the reason `lib/icons.ts` gives: `tsc --noEmit` is the only static gate
 * the SPA has, so a step naming a figure that does not exist must fail the build rather than draw an
 * empty box. The drawings are in `components/figures`, keyed by the same union, so the two can only
 * disagree at compile time.
 *
 * Unlike an `Icon`, a figure carries meaning, so every one has a label - the text a screen reader
 * reads in its place. The step or entry beside it still has to make sense without it: a figure makes
 * a sentence quicker to take in, it never replaces one.
 */
export type FigureId =
  | 'flow'
  | 'dictionary-rule'
  | 'revision-publish'
  | 'policy-anatomy'
  | 'policy-builder'
  | 'save-approve-publish'
  | 'bind-by-reference'
  | 'kit-contents'
  | 'install-as-user'
  | 'checkin-window'
  | 'case-folding'
  | 'trust-anchor'

export const FIGURES: Record<FigureId, { label: string }> = {
  'flow': {
    label:
      'A dictionary is bound by a policy, the policy is published to devices, and what devices report becomes cases.'
  },
  'dictionary-rule': {
    label:
      'A rule is a name, a list of terms and a severity. Trying it against sample text shows which terms match.'
  },
  'revision-publish': {
    label: 'A draft becomes a frozen, published revision. The next edit starts a new draft.'
  },
  'policy-anatomy': {
    label:
      'The policy editor has five sections: name, capability, what it looks for, when it runs, and what happens with the result.'
  },
  'policy-builder': {
    label:
      'Three choices - an assistant, where it runs, and what to do - become one policy for each place it runs, kept together as one group.'
  },
  'save-approve-publish': {
    label: 'Save makes a revision, a person approves it, and publishing sends it to every enrolled device.'
  },
  'bind-by-reference': {
    label:
      'A policy holds a reference to a published dictionary revision rather than a copy of its terms.'
  },
  'kit-contents': {
    label:
      'A kit is a zip holding the installer, install.ps1, a manifest with the published policies, and a live enrollment token.'
  },
  'install-as-user': {
    label:
      'Run the installer as the person who uses the machine. Not as an administrator, and never as SYSTEM.'
  },
  'checkin-window': {
    label:
      'A device checks in on an interval and its credential lasts a set period. A machine switched off for longer than that cannot come back by itself.'
  },
  'case-folding': {
    label:
      'Detections from one device, assistant and rule set within a quarter of an hour fold into a single case with a count.'
  },
  'trust-anchor': {
    label:
      'The organization signing anchor signs every policy, and each device accepts only policies signed by the anchor it pinned at enrollment.'
  }
}

/**
 * A figure as a step or entry names it: by id alone, or with the parts of it to draw in the accent.
 *
 * `focus` exists for the policy editor's five sections, where the same drawing serves four steps and
 * what changes is which section the step is about. One-based, to match the section numbers the
 * editor itself prints.
 */
export type FigureSpec = FigureId | { id: FigureId; focus?: readonly number[] }

export function figureRef(spec: FigureSpec): { id: FigureId; focus: readonly number[] } {
  return typeof spec === 'string' ? { id: spec, focus: [] } : { id: spec.id, focus: spec.focus ?? [] }
}
